import React from 'react';
import Icon from './Icon';
import Modal from './Modal';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' shows a red icon + red confirm button. 'primary' uses the brand pink. */
  variant?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const variantConfig: Record<
  'danger' | 'primary',
  { icon: string; iconBg: string; iconColor: string; btnClass: string }
> = {
  danger: {
    icon: 'x-circle',
    iconBg: 'var(--red-bg)',
    iconColor: 'var(--red)',
    btnClass: 'btn btn-danger',
  },
  primary: {
    icon: 'check-circle',
    iconBg: 'var(--brand-bg)',
    iconColor: 'var(--brand)',
    btnClass: 'btn btn-primary',
  },
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const cfg = variantConfig[variant];

  return (
    <Modal open={open} title={title} onClose={busy ? () => undefined : onCancel} width={480}>
      <div className="flex flex-col items-center text-center">
        <div
          className="rounded-2xl flex items-center justify-center mb-4"
          style={{
            width: 64,
            height: 64,
            background: cfg.iconBg,
          }}
        >
          <Icon name={cfg.icon} size={28} color={cfg.iconColor} />
        </div>
        <div
          className="text-[15px] text-g600 leading-relaxed mb-6"
          style={{ maxWidth: 320 }}
        >
          {message}
        </div>
        <div className="flex gap-2.5 justify-center w-full">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={cfg.btnClass}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
